import React from 'react';
import {useField, useFormikContext} from 'formik';
import {FormattedMessage, useIntl} from 'react-intl';
import InputText from '../../../../common/components/ui/InputText';
import InputCheckbox from '../../../../common/components/ui/InputCheckbox';
import ButtonAction from '../../../../common/components/ui/ButtonAction';
import {OrderInvoiceFormValues} from '../../types';
import {Format} from '../../../../common/utils';
import remove from '../../../../common/assets/icons/trash.svg';
import styles from './FieldLine.module.scss';
import {useOrderInvoiceFormContext} from "../../contexts/OrderInvoiceFormContext";

export interface FieldLineProps {
  lineIndex: number;
  onRemove?: React.MouseEventHandler<HTMLButtonElement>;
}

const FieldLine = ({
                     lineIndex,
                     onRemove,
                   }: FieldLineProps) => {
  const [fieldVAT, metaVAT] = useField<boolean>({name: `lines.${lineIndex}.vatIncluded`});
  const [fieldAmount, metaAmount, helperAmount] = useField<string>({name: `lines.${lineIndex}.amount`});
  const [fieldDescription, metaDescription] = useField<string>({name: `lines.${lineIndex}.description`});
  const {values} = useFormikContext<OrderInvoiceFormValues>();
  const {showAllErrors} = useOrderInvoiceFormContext();
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    helperAmount.setValue(Format.formatToFloatValue(e));
  };
  const handleAmountBlur = () => {
    helperAmount.setValue(Format.formatValueToFixed(fieldAmount.value, 2));
  };

  const intl = useIntl();
  const labelDescription = intl.formatMessage({
    id: 'orderInvoice-form-description',
    defaultMessage: 'Description'
  });
  const defaultPlaceholder = intl.formatMessage({
    id: 'default-placeholder',
    defaultMessage: 'Write here'
  });
  const labelAmount = intl.formatMessage({
    id: 'orderInvoice-form-amount',
    defaultMessage: 'Amount'
  });
  const placeholderAmount = intl.formatMessage({
    id: 'placeholder-amount',
    defaultMessage: 'Write an amount'
  });
  const descLimit = '100';

  return (
    <div className={styles.lineContainer}>
      <div className={styles.colLarge}>
        <InputText
          {...fieldDescription}
          {...metaDescription}
          id={fieldDescription.name}
          variant={'full'}
          aria-label='description'
          label={[labelDescription, `(${fieldDescription.value.length}/${descLimit})`].filter(Boolean).join(' ')}
          placeholder={defaultPlaceholder}
          maxlength={descLimit}
          showErrorOnSubmit={showAllErrors}
        />
      </div>
      <div className={styles.colMedium}>
        <InputText
          {...fieldAmount}
          {...metaAmount}
          id={fieldAmount.name}
          variant={'full'}
          defaultValue={fieldAmount.value}
          onChange={handleAmountChange}
          onBlur={handleAmountBlur}
          aria-label='amount'
          label={labelAmount}
          placeholder={placeholderAmount}
          showErrorOnSubmit={showAllErrors}
        />
      </div>
      <div className={styles.colMedium}>
        <InputCheckbox
          {...fieldVAT}
          {...metaVAT}
          id={fieldVAT.name}
          aria-label='vat'
          checked={fieldVAT.value}
          defaultChecked={fieldVAT.value}
          label={(
            <FormattedMessage
              id={'orderInvoice-form-vatIncluded'}
              defaultMessage={'VAT included'}
            />
          )}
        />
      </div>
      <div className={styles.removeButtonContainer}>
        <ButtonAction
          variant={'link'}
          type={'button'}
          label={(
            <img
              src={remove}
              alt="removeLine"
              className={styles.removeButton}
            />
          )}
          customClassnames={{container: styles.removeButton}}
          onClick={onRemove}
          disabled={values.lines.length <= 1}
        />
      </div>
    </div>
  );
};

export default FieldLine;
